import ArrowDownWhiteSVG from 'assets/ver2/icons/arrow-down-icon-white.svg';
import ArrowDownBlackSVG from 'assets/ver2/icons/arrow-down-icon.svg';
import ButtonCommon from 'components/common/ButtonCommon';
import SizeBox from 'components/common/SizeBox';
import {
  COUNTRY_FLAG_REFERENCES,
  ORDER_EMAIL,
  TRANSACTION_ADD_PREFIX,
} from 'constants';
import { format } from 'helpers';
import Cookies from 'helpers/cookies';
import { MAKE_PAYMENT_KEY } from 'hooks/form-add-transaction/useAddTransaction';
import { FX_MAKE_PAYMENT_KEY } from 'hooks/fx/useFxAddTransaction';
import useLang from 'hooks/useLang';
import useStore from 'hooks/useStore';
import { isEmpty, isObject } from 'lodash';
import { useEffect, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { paragraphMixin } from 'styles';

const ExchangeRateAccordionItem = ({
  eventKey,
  country,
  countryName,
  isActive,
  onClick,
}) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const { state } = useStore();
  const { remittanceRatesSelected } = state || {};

  const [isShowRates, setShowRates] = useState(false);
  useEffect(() => {
    if (isActive) {
      const timer = setTimeout(() => {
        setShowRates(true);
        clearTimeout(timer);
      }, 300);
    } else {
      setShowRates(false);
    }
  }, [isActive]);

  const accordionItemClassName = `exchang-rate-accordion-item-${eventKey}`;

  const handleOnClick = () => {
    Cookies.remove(MAKE_PAYMENT_KEY);
    Cookies.remove(FX_MAKE_PAYMENT_KEY);
    Cookies.remove(ORDER_EMAIL);
    navigate(TRANSACTION_ADD_PREFIX);
  };

  const getReceivedCurrenciesLabel = (num) => {
    if (isActive) return t('label_receiving_currency');
    if (num === 1) {
      return t('label_received_currency', {
        number: num,
      });
    }
    return t('label_received_currencies', {
      number: num,
    });
  };

  const renderCurrencies = (rates, optional = {}) => {
    const { formattedRateStyle } = optional || {};
    if (isEmpty(rates))
      return <NoRates>{t('label_no_rates_available')}</NoRates>;
    return (
      <>
        {Object.keys(rates).map((currencyCode) => {
          const rateInfo = rates[currencyCode];
          if (
            rateInfo.rate &&
            typeof rateInfo.rate === 'number' &&
            !isNaN(rateInfo.rate)
          ) {
            const formattedRate = format.toAmountStr2(rateInfo.rate);
            return (
              <div key={currencyCode}>
                <span>{rateInfo.currencyCode}</span>
                <br />
                <SizeBox height={4} />
                <span className="rate" style={formattedRateStyle || {}}>
                  {formattedRate}
                </span>
              </div>
            );
          }
          return (
            <div key={currencyCode}>
              <span>{currencyCode}</span>
              <br />
              <SizeBox height={4} />
              <span className="rate" style={formattedRateStyle || {}}>
                {0}
              </span>
            </div>
          );
        })}
      </>
    );
  };

  return (
    <AccordionItem
      className={accordionItemClassName}
      onClick={onClick}
      eventKey={eventKey}
    >
      <AccordionHeader $isActive={isActive}>
        <AccordionHeaderText $isActive={isActive}>
          <AccordionCountryItem $isActive={isActive}>
            <AccordionFlagIcon
              style={{ position: 'unset' }}
              src={COUNTRY_FLAG_REFERENCES[country]}
            />
            <AccordionCountryLabel $isActive={isActive}>
              <p className="country-name">{countryName}</p>
            </AccordionCountryLabel>
          </AccordionCountryItem>
        </AccordionHeaderText>
        <AccordionHeaderBody $isActive={isActive}>
          {isShowRates && (
            <>
              <SizeBox height={4} />
              <Desc>
                {getReceivedCurrenciesLabel(
                  isObject(remittanceRatesSelected) &&
                    Object.keys(remittanceRatesSelected)?.length
                )}
              </Desc>
              <Desc style={{ fontSize: '12px' }}>
                ({t('label_exchange_rate_per_aud')})
              </Desc>
              <CurrenciesWrap>
                <Currencies $isActive={isActive}>
                  {renderCurrencies(remittanceRatesSelected, {
                    formattedRateStyle: {
                      fontSize: '20px',
                      fontWeight: 500,
                    },
                  })}
                </Currencies>
              </CurrenciesWrap>
              {!isEmpty(remittanceRatesSelected) && (
                <ButtonCommon
                  value={t('button_send_now_2')}
                  color="var(--ds-c-blue)"
                  background="var(--ds-c-white)"
                  styles={{
                    height: '44px',
                    width: '150px',
                    position: 'absolute',
                    bottom: '10px',
                    left: 'calc(50% - 75px)',
                    borderRadius: '100px',
                  }}
                  onClick={handleOnClick}
                />
              )}
            </>
          )}
        </AccordionHeaderBody>
      </AccordionHeader>
    </AccordionItem>
  );
};

const CurrenciesWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  height: fit-content;
`;

const Currencies = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 4px;
  margin: 8px 0 0 0;
  font-weight: 700;
  width: 100%;

  & span {
    background: #fff;
    border: 1px solid var(--ds-c-black);
    border-radius: 10px;
    padding: 0 5px;
    line-height: 20px;
  }

  ${(props) =>
    props.$isActive
      ? `
      flex-direction: row;
      justify-content: flex-start;
      width: 100%;
      font-size: 16px;
      gap: 0px;
      margin: 20px 0 0 0;
      
      & div {
        width: 33.33%;
      }

      & span {
        border: none;
        background: transparent;
        color: var(--c-quaternary);
        height: fit-content;
        line-height: 100%;
        font-size: 14px;
        font-weight: 500;
        text-transform: uppercase;
        padding: 0;
        margin-bottom: 2px;
      }
      & span.rate {
        font-size: 24px;
        color: #fff;
      }
      `
      : `
      margin-bottom: 26px;
    `};
`;

const Desc = styled.p`
  ${paragraphMixin};
  margin: 0;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  color: #fff;
`;

const AccordionItem = styled(Accordion.Item)`
  border: none;
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0px;
  }
`;
const AccordionHeader = styled(Accordion.Header)`
  height: fit-content;
  padding: 0;
  box-shadow: none;
  border-radius: 10px;

  & button {
    padding: 0;
    background-color: var(--ds-c-white) !important;
    box-shadow: none !important;
    display: block;
    border-radius: 10px;

    &:focus,
    &:hover {
      z-index: 1;
    }

    &::after,
    &:not(.collapsed)::after {
      background-image: ${(props) =>
        props.$isActive
          ? `url(${ArrowDownWhiteSVG})`
          : `url(${ArrowDownBlackSVG})`};
      background-position: center;
      background-size: 20px 20px;

      position: absolute;
      top: 15px;
      right: 10px;
    }
  }
`;
const AccordionHeaderText = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-grey-dark);
  transition: 0.2s;

  padding: 0;
  margin: 0;
  height: fit-content;
  display: flex;
  align-items: center;
  background: ${(props) => (props.$isActive ? '#081933' : 'transparent')};
  border-radius: 10px 10px 0 0;
`;
const AccordionCountryItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  padding-inline: 16px;
  width: 100%;
  height: 50px;
  border-top: none;
  cursor: pointer;
  border-radius: ${(props) => (props.$isActive ? '10px 10px 0 0' : '10px')};
  margin-bottom: 8px;
  background: ${(props) => (props.$isActive ? '#081933' : '#fff')};
  transition: 0.2s;

  &:first-child {
  }

  &:last-child {
    margin-bottom: 0px;
  }
`;
const AccordionCountryLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0px;

  & p {
    margin: 0;
    padding: 0;

    font-family: var(--ff-primary);
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 100%;
    text-align: left;
    text-transform: uppercase;
    color: #000;

    &.country-name {
      font-size: 14px;
      line-height: 100%;
      color: ${(props) => (props.$isActive ? '#fff' : '#000')};
    }
  }
`;
const AccordionFlagIcon = styled.img`
  width: 30px;
  height: 30px;
  object-fit: cover;
  border: 1px solid var(--ds-bg-2);
  border-radius: 50%;
  margin-right: 8px;

  position: absolute;
  bottom: 11px;
  left: 0;
`;
const AccordionHeaderBody = styled.div`
  padding-inline: 10px;
  background: ${(props) => (props.$isActive ? '#081933' : 'transparent')};
  height: ${(props) => (props.$isActive ? '175px' : 0)};
  border-radius: 0 0 10px 10px;
  transition: 0.2s;
`;

const NoRates = styled.div`
  ${paragraphMixin};
  font-weight: 500;
  width: 100% !important;
  text-align: center;
  color: #fff;
`;

export default ExchangeRateAccordionItem;
